import { useCallback, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate, useParams } from "@tanstack/react-router"
import {
	IconPen2Outline18 as Edit2,
	IconTrashOutline18 as Trash,
} from "nucleo-ui-outline-18"
import { useToggle } from "react-use"

import type { ProjectIconKeys } from "@productlane/ui"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import {
	AlertDialog,
	Button,
	Empty,
	IconButton,
	ScrollArea,
	ScrollViewport,
} from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { EllipsisVerticalIcon as MoreVertical } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { ConvertTagKind } from "./convert-tag-copy"
import EmptyTags from "@/assets/empty-states/empty-tags.png"
import { hexToRGB } from "@/components/color-picker"
import {
	DetailScreenEditor,
	DetailThreadsList,
	hasActiveDetailFilters,
	useDetailThreadsControls,
} from "@/components/detail-screen"
import { DetailThreadsToolbar } from "@/components/detail-screen/detail-threads-toolbar"
import { useDetailListState } from "@/components/detail-screen/use-detail-list-state"
import { MetaInfo } from "@/components/meta-info"
import { SubscribeButton } from "@/components/notifications/subscribe-button"
import { NotifyRecipients } from "@/components/shared/notify-recipients"
import { RoleCheck } from "@/components/shared/role-check"
import { useShortcut } from "@/lib"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import {
	useBackNavigation,
	useNavigationState,
	usePreviousNavEntry,
	useSetBackNavigation,
} from "@/lib/back-navigation"
import { hasLinearConnection } from "@/lib/linear-connection"
import { getProjectIcon } from "@/lib/projects"
import { isQuerySettled } from "@/lib/query-settled"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { HowItWorksButton } from "@/screens/settings/settings-switches/how-it-works-button"
import { CreateIssueDialog } from "../issues/create-issue-dialog"
import { DetailBreadcrumb, DetailTopBar } from "../page-header"
import { CreateProjectDialog } from "../projects/create-project-dialog"
import { ConvertTagActions } from "./convert-tag-actions"
import { TagDetailSidebar } from "./tag-detail-sidebar"
import { TagNote } from "./tag-note"
import { UpdateTagDialog } from "./update-tag-dialog"

export const TagsDetailScreen = () => {
	const navigate = useNavigate()

	const zero = useZero()
	const params = useParams({ strict: false })
	const id = params.id!
	useSetBackNavigation({
		type: "opportunities-detail",
		url: `/tags/${id}`,
	})
	const { setPages } = useNavigationState()
	const previousEntry = usePreviousNavEntry("opportunities-detail")

	const [tag, tagResult] = useQuery(queries.tagDetail(id))
	const [tagGroups] = useQuery(queries.tagGroups())
	const [teamMembers] = useQuery(queries.teamMembers())
	// One grouped read for the sidebar; the thread list stays on Zero.
	const statsQuery = api.tag.detailStats.useQuery(
		{ tagId: id },
		{ staleTime: 30_000 },
	)
	const stats = statsQuery.data

	const tagGroup = useMemo(() => {
		if (!tag) return undefined
		return tagGroups.find((g) => g.id === tag.tagGroupId)
	}, [tag, tagGroups])
	const tagGroupColor = tagGroup?.color

	const [isConfirmDeleteOpen, setConfirmDelete] = useToggle(false)
	const [isUpdateTagOpen, setIsUpdateTagOpen] = useToggle(false)
	const { role } = useRole()
	const threadsState = useDetailListState()
	const threadControls = useDetailThreadsControls()
	const workspace = useZeroWorkspace()

	useShortcut(
		"e",
		() => {
			analytics.track("open-update-opportunity-dialog", {
				id: tag?.id,
				shortcut: true,
			})
			setIsUpdateTagOpen(true)
		},
		{ preventDefault: true, enabled: role !== "VIEWER" },
	)
	useShortcut(
		"meta+backspace, ctrl+backspace",
		() => {
			analytics.track("open-delete-opportunity-dialog", {
				id: tag?.id,
				shortcut: true,
			})
			setConfirmDelete(true)
		},
		{ enabled: role !== "VIEWER" },
	)

	const { goBack } = useBackNavigation()
	const close = useCallback(
		(shortcut = false) => {
			if (tag) {
				goBack("/threads")
				analytics.track("tag-close", { id: tag.id, shortcut })
			}
		},
		[goBack, tag],
	)
	useShortcut(
		"esc",
		() => {
			close(true)
		},
		{ enabled: !isConfirmDeleteOpen },
	)

	const [isProjectDialogOpen, setProjectDialogOpen] = useState(false)
	const [isIssueDialogOpen, setIssueDialogOpen] = useState(false)

	const openConvert = useCallback((kind: ConvertTagKind) => {
		if (kind === "project") setProjectDialogOpen(true)
		else setIssueDialogOpen(true)
	}, [])

	if (!tag) {
		if (!isQuerySettled(tagResult)) return null
		return <EmptyTagDetailsScreen />
	}

	const TagIcon = getProjectIcon(tag.icon)
	const showToolbar =
		threadsState.size > 0 || hasActiveDetailFilters(threadControls.filters)
	return (
		<>
			<MetaInfo title="Tag Detail" />
			<AlertDialog
				isOpen={isConfirmDeleteOpen}
				onOpenChange={(value) => {
					setConfirmDelete(value)
				}}
				confirmText="Delete"
				title="Are you sure?"
				description="Do you really want to delete this tag?"
				onCancel={() => {
					setConfirmDelete(false)
					analytics.track("cancel-delete-opportunity", { id: tag.id })
				}}
				onConfirm={async () => {
					void navigate({ to: "/threads", replace: true })
					await mutateOk(zero.mutate(mutators.tags.delete({ id: tag.id })))
					analytics.track("delete-tag", { id: tag.id })
				}}
			/>
			<UpdateTagDialog
				isOpen={isUpdateTagOpen}
				onOpenChange={setIsUpdateTagOpen}
				values={{
					id: tag.id,
					icon: tag.icon as ProjectIconKeys,
					name: tag.name,
					tagGroupId: tag.tagGroupId,
				}}
			/>
			<CreateProjectDialog
				isOpen={isProjectDialogOpen}
				onOpenChange={(value, created) => {
					setProjectDialogOpen(value)
					if (created) {
						void navigate({
							to: "/projects/$id",
							params: { id: created.id },
							replace: true,
						})
						setPages([
							{
								type: "projects",
								url: "/projects",
							},
							{
								type: "projects-detail",
								url: `/projects/${created.id}`,
							},
						])
					}
				}}
				title={tag.name}
				defaultColor={tagGroupColor}
				defaultIcon={tag.icon as ProjectIconKeys}
				tagId={tag.id}
			/>
			<CreateIssueDialog
				isOpen={isIssueDialogOpen}
				onOpenChange={(value, created) => {
					setIssueDialogOpen(value)
					if (created) {
						void navigate({
							to: "/issues/$id",
							params: { id: created.id },
							replace: true,
						})
						setPages([
							{
								type: "issues",
								url: "/issues",
							},
							{
								type: "issues-detail",
								url: `/issues/${created.id}`,
							},
						])
					}
				}}
				title={tag.name}
				tagId={tag.id}
			/>
			<DetailTopBar className="justify-between" clearsNavToggle>
				<div className="flex min-w-0 items-center gap-1">
					<DetailBreadcrumb
						parentLabel={previousEntry?.label ?? "Tags"}
						onParentClick={() => close()}
						currentIcon={
							<TagIcon
								className="h-3.5 w-3.5 flex-none"
								style={{ color: tagGroupColor }}
							/>
						}
						currentLabel={tag.name}
					/>
					<RoleCheck not={["VIEWER"]}>
						<DropdownMenu
							trigger={
								<IconButton
									Icon={MoreVertical}
									variant="ghost"
									iconClassName="fill-current"
									aria-label="Tag actions"
								/>
							}
							contentProps={{ side: "bottom", align: "start" }}
						>
							<DropdownMenuGroup>
								<DropdownMenuItem
									Icon={Edit2}
									shortcut="E"
									onClick={() => {
										setIsUpdateTagOpen(true)
										analytics.track("open-update-opportunity-dialog", {
											id: tag.id,
											shortcut: false,
										})
									}}
								>
									Edit
								</DropdownMenuItem>
								<DropdownMenuItem
									Icon={Trash}
									shortcut={`${ShortcutSymbols.CMD}${ShortcutSymbols.BACKSPACE}`}
									onClick={() => {
										setConfirmDelete(true)
										analytics.track("open-delete-opportunity-dialog", {
											id: tag.id,
											shortcut: false,
										})
									}}
								>
									Delete
								</DropdownMenuItem>
							</DropdownMenuGroup>
						</DropdownMenu>
					</RoleCheck>
				</div>
				<div className="flex items-center gap-1.5">
					<RoleCheck not={["VIEWER"]}>
						{hasLinearConnection(workspace) && (
							<ConvertTagActions
								threadCount={stats?.threadCount}
								onConvert={openConvert}
							/>
						)}
						<NotifyRecipients
							entity={{
								id: tag.id,
								name: tag.name,
								type: "tag",
							}}
						/>
					</RoleCheck>
					<SubscribeButton entityType="tag" entityId={tag.id} />
				</div>
			</DetailTopBar>
			<div className="flex min-h-0 flex-1 overflow-hidden">
				<DetailScreenEditor className="min-w-0 flex-1">
					<div className="flex flex-col gap-7 pb-12">
						<div className="flex flex-col gap-3">
							<div className="flex flex-wrap items-center gap-3.5">
								<div
									className="flex h-10 w-10 flex-none items-center justify-center rounded-[10px] bg-opacity-10"
									style={{
										backgroundColor: tagGroupColor
											? hexToRGB(tagGroupColor, "bg")
											: undefined,
									}}
								>
									<TagIcon
										className="h-5 w-5 flex-none"
										style={{ color: tagGroupColor }}
									/>
								</div>
								<h1
									className="min-w-0 flex-1 text-2xl font-semibold"
									style={{ overflowWrap: "anywhere" }}
								>
									{tag.name}
								</h1>
							</div>

							<TagNote tagId={tag.id} note={tag.summaryString} />
						</div>

						<div className="flex flex-col">
							{showToolbar && (
								<div className="mb-2 flex min-h-7 items-center justify-end">
									<DetailThreadsToolbar
										sortOrder={threadControls.sortOrder}
										onSortOrderChange={threadControls.setSortOrder}
										filters={threadControls.filters}
										onFiltersChange={threadControls.setFilters}
										teamMembers={teamMembers}
									/>
								</div>
							)}
							<DetailThreadsList
								tagId={id}
								setThreadsSize={threadsState.report}
								controls={threadControls}
								hideLabel
								flush
							/>
						</div>

						{threadsState.isEmpty && (
							<div className="flex min-h-[40dvh] w-full items-center justify-center py-8">
								<Empty
									text="There are no threads linked to this tag yet. Tag observations over time and convert them to Linear issues or projects quickly."
									title="Tags"
									className="max-w-[20rem]"
									variant="left"
									image={
										<img
											src={EmptyTags}
											alt="Empty"
											className="-ml-2"
											style={{ transform: "scaleX(-1)" }}
										/>
									}
									action={
										<div className="flex items-center gap-4">
											<Button
												onClick={() => {
													void navigate({ to: "/threads" })
												}}
												className="min-w-32"
											>
												Go to Inbox
											</Button>
											<HowItWorksButton
												className="min-h-7"
												article="21068862-387e-4231-bddf-c15abae813ff"
											/>
										</div>
									}
								/>
							</div>
						)}
					</div>
				</DetailScreenEditor>
				<aside
					aria-label="Tag details"
					className="hidden h-full w-[18rem] flex-none border-l border-gray-200 lg:block dark:border-gray-700/80"
				>
					<ScrollArea>
						<ScrollViewport>
							<TagDetailSidebar
								stats={stats}
								createdAt={tag.createdAt}
								groupName={tagGroup?.name}
								groupColor={tagGroupColor}
								onEditGroup={
									role === "VIEWER" ? undefined : () => setIsUpdateTagOpen(true)
								}
							/>
						</ScrollViewport>
					</ScrollArea>
				</aside>
			</div>
		</>
	)
}

const EmptyTagDetailsScreen = () => {
	return (
		<div className="flex min-h-0 w-full flex-1 items-center justify-center">
			<Empty
				text="It may have been moved, deleted, or you may not have access to it."
				title="Tag not found"
				className="max-w-[20rem]"
				variant="center"
				image={<img src={EmptyTags} alt="Tag not found" />}
			/>
		</div>
	)
}
